import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import useFetch from "../../hooks/useFetch";
import styles from "./DataTable.module.css";


// At the top of DataTable.jsx, add this modal component
const ConfirmModal = ({ onConfirm, onCancel, itemName }) => (
  <div style={{
    position: "fixed", inset: 0, zIndex: 9999,
    background: "rgba(0,0,0,0.3)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 20,
  }}>
    <div style={{
      background: "#fff", borderRadius: 16, padding: "28px 28px 24px",
      width: "100%", maxWidth: 380, boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
      border: "1px solid #ebebeb", animation: "modalIn .18s ease",
    }}>
      {/* Icon */}
      <div style={{
        width: 44, height: 44, borderRadius: 12,
        background: "#fef2f2", border: "1px solid #fecaca",
        display: "flex", alignItems: "center", justifyContent: "center",
        marginBottom: 16,
      }}>
        <TrashIcon color="#dc2626" size={18} />
      </div>

      {/* Text */}
      <div style={{ fontSize: 15, fontWeight: 600, color: "#0a0a0a", marginBottom: 6, letterSpacing: "-0.02em" }}>
        Delete this entry?
      </div>
      <div style={{ fontSize: 13, color: "#a3a3a3", lineHeight: 1.5, marginBottom: 24 }}>
        <span style={{ color: "#525252", fontWeight: 500 }}>"{itemName}"</span> will be permanently removed. This action cannot be undone.
      </div>

      {/* Buttons */}
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={onCancel}
          style={{
            flex: 1, height: 38, borderRadius: 8,
            border: "1px solid #ebebeb", background: "#fff",
            fontSize: 13, fontWeight: 500, color: "#525252",
            cursor: "pointer", fontFamily: "inherit", transition: "all .14s",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "#d4d4d4"; e.currentTarget.style.color = "#0a0a0a"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "#ebebeb"; e.currentTarget.style.color = "#525252"; }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          style={{
            flex: 1, height: 38, borderRadius: 8,
            border: "1px solid #dc2626", background: "#dc2626",
            fontSize: 13, fontWeight: 500, color: "#fff",
            cursor: "pointer", fontFamily: "inherit", transition: "all .14s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "#b91c1c"; e.currentTarget.style.borderColor = "#b91c1c"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#dc2626"; e.currentTarget.style.borderColor = "#dc2626"; }}
        >
          Delete
        </button>
      </div>
    </div>
  </div>
);

const DataTable = ({ columns, collapsed }) => {
  const location = useLocation();
  const path = location.pathname.split("/")[1];
  const { data, loading } = useFetch(`https://api.moroccool.com/api/${path}`);

  // Analytics is only wired up for cafes/hotels, since only those have
  // analytics endpoints on the backend. Singular form matches the
  // /analytics/:businessType/:businessId route (cafe, hotel).
  const analyticsEnabled = path === "cafes" || path === "hotels";
  const businessType = path.slice(0, -1);

  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [prov, setProv] = useState("all");
  const [page, setPage] = useState(1);
  const [per, setPer] = useState(10);
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState(-1);
  const [selected, setSelected] = useState(new Set());
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => { window.dispatchEvent(new Event("resize")); }, [collapsed]);
  useEffect(() => { if (data) setRows(data); }, [data]);

  const pageTitle = path.charAt(0).toUpperCase() + path.slice(1);

  /* ── filter + sort ── */
  const filtered = rows
    .filter((u) => {
      const q = query.toLowerCase();
      const matchQ =
        !q ||
        Object.values(u).some((v) =>
          String(v).toLowerCase().includes(q)
        );
      const matchP = prov === "all" || u.oauthProvider === prov;
      return matchQ && matchP;
    })
    .sort((a, b) => {
      let av = a[sortKey] ?? "", bv = b[sortKey] ?? "";
      if (sortKey === "createdAt") { av = new Date(av); bv = new Date(bv); }
      return av > bv ? sortDir : av < bv ? -sortDir : 0;
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / per));
  const safePage = Math.min(page, totalPages);
  const slice = filtered.slice((safePage - 1) * per, safePage * per);

  /* ── handlers ── */
  const handleDelete = async (id, name) => {
    setConfirmModal({ id, name });
  };

  const confirmDelete = async () => {
    const { id } = confirmModal;
    setConfirmModal(null);
    try {
      await axios.delete(`https://api.moroccool.com/api/${path}/${id}`);
      setRows((prev) => prev.filter((r) => r._id !== id));
      setSelected((prev) => { const s = new Set(prev); s.delete(id); return s; });
    } catch (err) {
      console.error(err);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => d * -1);
    else { setSortKey(key); setSortDir(-1); }
    setPage(1);
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const toggleAll = (checked) => {
    setSelected((prev) => {
      const s = new Set(prev);
      slice.forEach((r) => checked ? s.add(r._id) : s.delete(r._id));
      return s;
    });
  };

  const allOnPageSelected = slice.length > 0 && slice.every((r) => selected.has(r._id));
  const someOnPageSelected = slice.some((r) => selected.has(r._id));

  /* ── pagination builder ── */
  const buildPages = () => {
    const p = safePage, t = totalPages;
    const pages = [];
    if (t <= 7) {
      for (let i = 1; i <= t; i++) pages.push(i);
    } else {
      pages.push(1);
      if (p > 3) pages.push("...");
      for (let i = Math.max(2, p - 1); i <= Math.min(t - 1, p + 1); i++) pages.push(i);
      if (p < t - 2) pages.push("...");
      pages.push(t);
    }
    return pages;
  };

  /* ── action column (always last) ── */
  const actionCol = {
    field: "actions",
    headerName: "",
    width: 90,
    renderCell: (row) => (
      <div className={styles.acts}>
        {analyticsEnabled && (
          <Link to={`/analytics/${businessType}/${row._id}`}>
            <button className={styles.icBtn} aria-label="Analytics">
              <AnalyticsIcon />
            </button>
          </Link>
        )}
        <Link to={`/${path}/${row._id}`}>
          <button className={styles.icBtn} aria-label="Edit">
            <EditIcon />
          </button>
        </Link>
        <button
          className={`${styles.icBtn} ${styles.del}`}
          aria-label="Delete"
          onClick={() => handleDelete(row._id, row.name || row.username || row.title || row._id)}
        >
          <TrashIcon />
        </button>
      </div>
    ),
  };

  const allCols = [...columns, actionCol];

  /* ── KPI counts (only for users page) ── */
  const kpis = path === "users" ? [
    { label: "Total", val: rows.length, meta: "All providers" },
    { label: "Google", val: rows.filter((u) => u.oauthProvider === "google.com").length, meta: "OAuth" },
    { label: "Facebook", val: rows.filter((u) => u.oauthProvider === "facebook.com").length, meta: "OAuth" },
    { label: "Email", val: rows.filter((u) => u.oauthProvider === "email").length, meta: "Password" },
  ] : null;

  return (
    <div className={styles.page}>

      {/* ── Header ── */}
      <div className={styles.hd}>
        <div>
          <div className={styles.bc}>
            <span>Dashboard</span>
            <span className={styles.bcSep}>›</span>
            <span className={styles.bcCur}>{pageTitle}</span>
          </div>
          <h1 className={styles.h1}>{pageTitle}</h1>
          <p className={styles.h1Sub}>Manage your {path} efficiently</p>
        </div>
        <div className={styles.hdR}>
          <button className={`${styles.btn} ${styles.btnOutline}`}>
            <DownloadIcon /> <span>Export</span>
          </button>
          <Link to={`/${path}/new`} className={styles.btnLink}>
            <button className={`${styles.btn} ${styles.btnSolid}`}>
              <PlusIcon /> <span>Add new</span>
            </button>
          </Link>
        </div>
      </div>

      {/* ── KPI strip (users only) ── */}
      {kpis && (
        <div className={styles.kpiRow}>
          {kpis.map((k) => (
            <div className={styles.kpi} key={k.label}>
              <div className={styles.kpiLbl}>{k.label}</div>
              <div className={styles.kpiN}>{k.val}</div>
              <div className={styles.kpiMeta}>{k.meta}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Card ── */}
      <div className={styles.card}>

        {/* selection bar */}
        {selected.size > 0 && (
          <div className={styles.selBar}>
            <span className={styles.selBarL}><strong>{selected.size}</strong> selected</span>
            <div className={styles.selBarR}>
              <button className={styles.selAction} onClick={() => setSelected(new Set())}>Clear</button>
              <button className={`${styles.selAction} ${styles.selDanger}`}>Delete selected</button>
            </div>
          </div>
        )}

        {/* toolbar */}
        <div className={styles.tb}>
          <div className={styles.srch}>
            <SearchIcon />
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              placeholder={`Search ${path}…`}
            />
          </div>

          {/* provider filter — users only */}
          {path === "users" && (
            <div className={styles.seg}>
              {["all", "google.com", "facebook.com", "email"].map((p) => (
                <button
                  key={p}
                  className={`${styles.sg} ${prov === p ? styles.sgOn : ""}`}
                  onClick={() => { setProv(p); setPage(1); }}
                >
                  {p === "all" ? "All" : p === "google.com" ? "Google" : p === "facebook.com" ? "Facebook" : "Email"}
                </button>
              ))}
            </div>
          )}

          <button className={`${styles.btn} ${styles.btnOutline} ${styles.btnSm}`}>
            <FilterIcon /> <span>Filter</span>
          </button>
        </div>

        {/* table */}
        <div className={styles.tw}>
          {loading ? (
            <div className={styles.loadingState}>
              {[...Array(5)].map((_, i) => (
                <div key={i} className={styles.skelRow}>
                  {allCols.map((_, j) => <div key={j} className={styles.skel} />)}
                </div>
              ))}
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th className={styles.cbTh}>
                    <input
                      type="checkbox"
                      className={styles.cb}
                      checked={allOnPageSelected}
                      ref={(el) => { if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected; }}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                  </th>
                  {allCols.map((col) => (
                    <th
                      key={col.field}
                      style={{ minWidth: col.width || 120 }}
                      className={col.field === "actions" ? styles.thNoSort : ""}
                      onClick={() => col.field !== "actions" && handleSort(col.field)}
                    >
                      {col.headerName}
                      {col.field !== "actions" && (
                        <span className={styles.sortIc}>
                          {sortKey === col.field ? (sortDir === 1 ? " ↑" : " ↓") : " ↕"}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {slice.length === 0 ? (
                  <tr>
                    <td colSpan={allCols.length + 1} className={styles.emptyCell}>
                      <div className={styles.emptyIc}><EmptyIcon /></div>
                      <div className={styles.emptyT}>No results found</div>
                      <div className={styles.emptyS}>Try adjusting your search or filter</div>
                    </td>
                  </tr>
                ) : (
                  slice.map((row) => (
                    <tr key={row._id} className={selected.has(row._id) ? styles.rowSelected : ""}>
                      <td className={styles.cbTd}>
                        <input
                          type="checkbox"
                          className={styles.cb}
                          checked={selected.has(row._id)}
                          onChange={() => toggleRow(row._id)}
                        />
                      </td>
                      {allCols.map((col) => (
                        <td key={col.field} style={{ minWidth: col.width || 120 }}>
                          {col.renderCell
                            ? col.renderCell(row)
                            : col.valueGetter
                              ? col.valueGetter({ row })
                              : row[col.field] ?? "—"}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* footer */}
        <div className={styles.tfoot}>
          <div className={styles.tfootL}>
            <span className={styles.footTxt}>
              {filtered.length > 0
                ? `${(safePage - 1) * per + 1}–${Math.min(safePage * per, filtered.length)} of ${filtered.length}`
                : "No results"}
            </span>
            <label className={styles.perLabel}>
              Rows
              <select
                className={styles.perSel}
                value={per}
                onChange={(e) => { setPer(+e.target.value); setPage(1); }}
              >
                {[6, 10, 25, 50].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
          <div className={styles.pag}>
            <button
              className={styles.pgBtn}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              aria-label="Previous"
            >
              <ChevronLeftIcon />
            </button>
            {buildPages().map((p, i) =>
              p === "..." ? (
                <span key={`e${i}`} className={styles.pgEllipsis}>···</span>
              ) : (
                <button
                  key={p}
                  className={`${styles.pgBtn} ${safePage === p ? styles.pgActive : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              )
            )}
            <button
              className={styles.pgBtn}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              aria-label="Next"
            >
              <ChevronRightIcon />
            </button>
          </div>
        </div>
      </div>

      {/* Confirm delete modal */}
      {confirmModal && (
        <ConfirmModal
          itemName={confirmModal.name}
          onConfirm={confirmDelete}
          onCancel={() => setConfirmModal(null)}
        />
      )}
    </div>
  );
};

/* ── Inline SVG Icons ── */
const AnalyticsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);
const TrashIcon = ({ color = "currentColor", size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: "#a3a3a3" }}>
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const DownloadIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const FilterIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);
const ChevronLeftIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const EmptyIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: "#d4d4d4" }}>
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

export default DataTable;